import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Area, AreaChart, ComposedChart } from 'recharts';
import { RefreshCw, Trash, Zap, DatabaseIcon, Languages, Hash } from 'lucide-react';
import { useClientCache } from '@/hooks/use-client-cache';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { getCacheSizeMetrics, getCacheOptimizationRecommendations } from '@/services/cache/client-cache-service';
import { bytesToSize } from '@/utils/formatters';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getOptimalCacheSettings, isDeviceMemoryLow } from '@/utils/deviceUtils';

export const CacheAnalytics = () => {
  const { 
    metrics, 
    fetchCacheMetrics, 
    isLoadingMetrics, 
    clearCache,
    optimizeCache 
  } = useClientCache();

  const [sizeMetrics, setSizeMetrics] = useState<Record<string, number>>({});
  const [recommendations, setRecommendations] = useState<string[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('all');
  const [selectedStore, setSelectedStore] = useState<string>('all');
  const [isLowMemoryDevice, setIsLowMemoryDevice] = useState(false);

  useEffect(() => {
    fetchCacheMetrics();
    loadSizeMetrics();
    loadRecommendations();
    checkDeviceMemory();
    
    // Refresh metrics every 30 seconds
    const interval = setInterval(() => {
      fetchCacheMetrics();
      loadSizeMetrics();
    }, 30000);

    return () => clearInterval(interval);
  }, []);
  
  // Load cache size metrics
  const loadSizeMetrics = async () => {
    try {
      const sizes = await getCacheSizeMetrics();
      setSizeMetrics(sizes);
    } catch (error) {
      console.error('Error loading size metrics:', error);
    }
  };
  
  // Load optimization recommendations
  const loadRecommendations = async () => {
    try {
      const recs = await getCacheOptimizationRecommendations();
      setRecommendations(recs);
    } catch (error) {
      console.error('Error loading recommendations:', error);
    }
  };
  
  // Check if device has limited memory
  const checkDeviceMemory = () => {
    setIsLowMemoryDevice(isDeviceMemoryLow());
  };

  const handleClearCache = async () => {
    try {
      await clearCache();
      toast.success('Cache cleared successfully');
      // Reload metrics after clearing
      fetchCacheMetrics();
      loadSizeMetrics();
    } catch (error) {
      toast.error('Failed to clear cache');
    }
  };

  const handleOptimizeCache = async () => {
    try {
      await optimizeCache();
      toast.success('Cache optimized successfully');
      // Reload metrics and recommendations
      fetchCacheMetrics();
      loadSizeMetrics();
      loadRecommendations();
    } catch (error) {
      toast.error('Failed to optimize cache');
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Prepare chart data
  const prepareHitMissData = () => {
    return Object.entries(metrics).map(([store, data]) => ({
      name: getStoreName(store),
      hits: data.hits || 0,
      misses: data.misses || 0
    }));
  };

  const preparePieChartData = (store: string) => {
    if (!metrics[store]) return [];
    
    return [
      { name: 'Hits', value: metrics[store].hits || 0 },
      { name: 'Misses', value: metrics[store].misses || 0 }
    ];
  };
  
  // Prepare size metrics chart data
  const prepareSizeData = () => {
    return Object.entries(sizeMetrics).map(([store, size]) => ({
      name: getStoreName(store),
      size: size
    }));
  };
  
  // Prepare language-specific data
  const prepareLanguageData = () => {
    if (selectedStore === 'all') return [];
    
    const storeData = metrics[selectedStore];
    if (!storeData || !storeData.byLanguage) return [];
    
    return Object.entries(storeData.byLanguage).map(([lang, data]) => ({
      name: lang,
      hits: data.hits || 0,
      misses: data.misses || 0,
      hitRate: data.hits / (data.hits + data.misses || 1) * 100
    }));
  };
  
  // Get available languages across all stores
  const getAvailableLanguages = () => {
    const languages = new Set<string>();
    
    Object.values(metrics).forEach(storeMetrics => {
      if (storeMetrics.byLanguage) {
        Object.keys(storeMetrics.byLanguage).forEach(lang => languages.add(lang));
      }
    });
    
    return Array.from(languages);
  };

  const getStoreName = (storeKey: string) => {
    switch (storeKey) {
      case 'embeddings': return 'Embeddings';
      case 'responses': return 'Responses';
      case 'messages': return 'Messages';
      default: return storeKey;
    }
  };

  const COLORS = ['#0088FE', '#FF8042'];
  const SIZE_COLORS = {
    embeddings: '#8884d8',
    responses: '#82ca9d',
    messages: '#ffc658'
  };

  return (
    <Card className="w-full shadow-md">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Cache Analytics</span>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => {
              fetchCacheMetrics();
              loadSizeMetrics();
              loadRecommendations();
            }}
            disabled={isLoadingMetrics}
          >
            <RefreshCw className={`w-4 h-4 ${isLoadingMetrics ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
        <CardDescription>
          Monitor and manage client-side cache performance
          {isLowMemoryDevice && (
            <span className="ml-2 text-orange-500 font-medium">
              (Low memory device detected - using optimized cache settings)
            </span>
          )}
        </CardDescription>
      </CardHeader>

      <CardContent>
        {isLoadingMetrics ? (
          <div className="space-y-4">
            <Skeleton className="w-full h-[300px]" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Skeleton className="w-full h-[100px]" />
              <Skeleton className="w-full h-[100px]" />
              <Skeleton className="w-full h-[100px]" />
            </div>
          </div>
        ) : Object.keys(metrics).length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No cache metrics available yet.</p>
            <p className="text-sm mt-2">
              Cache metrics will appear here as the application is used.
            </p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => fetchCacheMetrics()}
            >
              Refresh Metrics
            </Button>
          </div>
        ) : (
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid grid-cols-4 mb-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="storage">Storage</TabsTrigger>
              <TabsTrigger value="language">Language</TabsTrigger>
              <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(metrics).map(([store, data]) => (
                  <Card key={store} className="overflow-hidden">
                    <CardHeader className="p-4 pb-0">
                      <CardTitle className="text-sm font-medium">{getStoreName(store)}</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Hit Rate</span>
                          <span className="text-sm font-medium">
                            {((data.hitRate || 0) * 100).toFixed(1)}%
                          </span>
                        </div>
                        <Progress value={(data.hitRate || 0) * 100} className="h-1.5" />
                        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
                          <span>Hits: {data.hits || 0}</span>
                          <span>Misses: {data.misses || 0}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {/* Hit Rate Bar Chart */}
              <Card>
                <CardHeader className="p-4 pb-0">
                  <CardTitle className="text-sm font-medium">Hit Rate Comparison</CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-2" style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={Object.entries(metrics).map(([store, data]) => ({
                        name: getStoreName(store),
                        hitRate: ((data.hitRate || 0) * 100).toFixed(1)
                      }))}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <XAxis dataKey="name" />
                      <YAxis unit="%" />
                      <Tooltip formatter={(value) => [`${value}%`, 'Hit Rate']} />
                      <Bar dataKey="hitRate" fill="#8884d8" name="Hit Rate (%)" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="storage" className="space-y-6">
              {/* Cache Storage Size Chart */}
              <Card>
                <CardHeader className="p-4 pb-0">
                  <CardTitle className="text-sm font-medium">Cache Storage Size</CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-2" style={{ height: '300px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={prepareSizeData()}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(value) => bytesToSize(value)} />
                      <Tooltip formatter={(value) => [bytesToSize(value as number), 'Size']} />
                      <Legend />
                      <Bar dataKey="size" name="Storage Size" fill="#8884d8">
                        {prepareSizeData().map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={SIZE_COLORS[entry.name.toLowerCase() as keyof typeof SIZE_COLORS] || '#8884d8'} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              
              {/* Compression Metrics */}
              <Card>
                <CardHeader className="p-4 pb-0">
                  <CardTitle className="text-sm font-medium">Compression Efficiency</CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="text-sm text-muted-foreground mb-2">
                    Showing compression ratio for vector embeddings
                  </div>
                  
                  {metrics.embeddings?.sizeMetrics?.avgCompressionRatio ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span>Average Compression Ratio</span>
                        <span className="font-medium">
                          {metrics.embeddings.sizeMetrics.avgCompressionRatio.toFixed(2)}x
                        </span>
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span>No Compression</span>
                          <span>Excellent</span>
                        </div>
                        <Progress 
                          value={Math.min(100, (metrics.embeddings.sizeMetrics.avgCompressionRatio / 10) * 100)} 
                          className="h-2"
                        />
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>1x</span>
                          <span>10x</span>
                        </div>
                      </div>
                      
                      <div className="bg-muted p-3 rounded text-sm">
                        <p>
                          <span className="font-medium">
                            {metrics.embeddings.sizeMetrics.avgCompressionRatio.toFixed(2)}x compression
                          </span>{' '}
                          means the cached embeddings take up{' '}
                          <span className="font-medium">
                            {(100 / metrics.embeddings.sizeMetrics.avgCompressionRatio).toFixed(1)}%
                          </span>{' '}
                          of their original size.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground">
                      <p>No compression metrics available yet.</p>
                      <p className="text-xs mt-1">
                        Compression metrics will appear when embedding vectors are cached.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="language" className="space-y-4">
              {/* Language selector */}
              <div className="flex space-x-4 mb-6">
                <div className="flex-1">
                  <label className="text-sm font-medium mb-2 block">Store</label>
                  <Select value={selectedStore} onValueChange={setSelectedStore}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a store" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Stores</SelectItem>
                      {Object.keys(metrics).map(store => (
                        <SelectItem key={store} value={store}>{getStoreName(store)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex-1">
                  <label className="text-sm font-medium mb-2 block">Language</label>
                  <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a language" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Languages</SelectItem>
                      {getAvailableLanguages().map(lang => (
                        <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Language-specific charts */}
              {selectedStore !== 'all' ? (
                <Card>
                  <CardHeader className="p-4 pb-0">
                    <CardTitle className="text-sm font-medium">
                      Language Metrics for {getStoreName(selectedStore)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-2" style={{ height: '350px' }}>
                    {prepareLanguageData().length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                          data={prepareLanguageData()}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <XAxis dataKey="name" />
                          <YAxis yAxisId="left" />
                          <YAxis yAxisId="right" orientation="right" unit="%" />
                          <CartesianGrid strokeDasharray="3 3" />
                          <Tooltip />
                          <Legend />
                          <Bar yAxisId="left" dataKey="hits" name="Hits" fill="#4ade80" />
                          <Bar yAxisId="left" dataKey="misses" name="Misses" fill="#f87171" />
                          <Line yAxisId="right" type="monotone" dataKey="hitRate" stroke="#8884d8" name="Hit Rate (%)" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        <p>No language-specific data available for this store</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.keys(metrics).map(store => {
                    const storeMetrics = metrics[store];
                    if (!storeMetrics?.byLanguage || Object.keys(storeMetrics.byLanguage).length === 0) {
                      return null;
                    }
                    
                    const languageData = Object.entries(storeMetrics.byLanguage).map(([lang, data]) => ({
                      name: lang,
                      hits: data.hits || 0,
                      misses: data.misses || 0
                    }));
                    
                    if (languageData.length === 0) return null;
                    
                    return (
                      <Card key={store}>
                        <CardHeader className="p-4 pb-0">
                          <CardTitle className="text-sm font-medium">{getStoreName(store)} by Language</CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-2" style={{ height: '200px' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={languageData}
                              margin={{ top: 10, right: 10, left: 10, bottom: 5 }}
                            >
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              <Bar dataKey="hits" fill="#4ade80" name="Hits" />
                              <Bar dataKey="misses" fill="#f87171" name="Misses" />
                            </BarChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="recommendations" className="space-y-4">
              <Card>
                <CardHeader className="p-4 pb-0">
                  <CardTitle className="text-sm font-medium">Cache Optimization Recommendations</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {recommendations.length > 0 ? (
                    <ul className="space-y-3">
                      {recommendations.map((rec, index) => (
                        <li key={index} className="bg-muted p-3 rounded flex">
                          <span className="text-primary mr-2">•</span>
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>No recommendations available.</p>
                      <p className="text-xs mt-1">
                        Run the cache optimization to generate recommendations.
                      </p>
                    </div>
                  )}
                  
                  <Button
                    variant="outline"
                    className="w-full mt-4"
                    onClick={handleOptimizeCache}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Analyze and Generate Recommendations
                  </Button>
                </CardContent>
              </Card>
              
              {/* Device Status */}
              <Card>
                <CardHeader className="p-4 pb-0">
                  <CardTitle className="text-sm font-medium">Device & Memory Status</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center">
                        <DatabaseIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                        Device Memory Profile
                      </span>
                      <span className={`font-medium ${isLowMemoryDevice ? 'text-amber-500' : 'text-green-500'}`}>
                        {isLowMemoryDevice ? 'Limited' : 'Standard'}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="flex items-center">
                        <Hash className="h-4 w-4 mr-2 text-muted-foreground" />
                        Total Cached Items
                      </span>
                      <span className="font-medium">
                        {Object.values(metrics).reduce((sum, m) => sum + (m.sets || 0), 0)}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="flex items-center">
                        <Languages className="h-4 w-4 mr-2 text-muted-foreground" />
                        Cached Languages
                      </span>
                      <span className="font-medium">
                        {getAvailableLanguages().length}
                      </span>
                    </div>
                    
                    {/* Optimal settings */}
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="text-sm font-medium mb-2">Optimal Cache Settings</h4>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <p>• In-memory items: {getOptimalCacheSettings().maxMemoryCacheItems}</p>
                        <p>• In-memory size: {bytesToSize(getOptimalCacheSettings().maxMemoryCacheSize)}</p>
                        <p>• Persisted items: {getOptimalCacheSettings().maxIndexedDBItems}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </CardContent>

      <CardFooter className="flex justify-end gap-2">
        <Button 
          variant="outline" 
          className="flex items-center gap-1"
          onClick={handleOptimizeCache}
        >
          <Zap className="w-4 h-4" />
          <span>Optimize Cache</span>
        </Button>
        <Button 
          variant="destructive" 
          className="flex items-center gap-1"
          onClick={handleClearCache}
        >
          <Trash className="w-4 h-4" />
          <span>Clear Cache</span>
        </Button>
      </CardFooter>
    </Card>
  );
};
